import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { createProduct, getProducts } from '../../../lib/api';
import {
  buildDefaultMenuVisibility,
  buildEmptySalesCatalog,
  createCatalogId,
  deriveLegacyMenusFromSalesCatalog,
  normalizeMenuVisibility,
  normalizeSalesCatalog,
  SALES_MENU_CHANNEL_OPTIONS,
  SALES_MENU_SCOPE_LABELS,
  SALES_MENU_SCOPE_OPTIONS,
} from '../../../lib/salesCatalog';
import { useManagerPortalConfig } from './useManagerPortalConfig';

const BAR_KEYWORDS = [
  'getränk',
  'getrank',
  'getraenk',
  'bier',
  'wein',
  'spritz',
  'cocktail',
  'longdrink',
  'spirituose',
  'kaffee',
  'cola',
  'limonade',
  'saft',
  'wasser',
  'tonic',
  'red bull',
  'prosecco',
  'vodka',
  'gin',
  'whisky',
  'rum',
  'espresso',
  'cappuccino',
  'latte',
  'americano',
];

function scopeTone(scope) {
  switch (scope) {
    case 'delivery_partner':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'delivery_internal':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'pickup':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function withSortOrder(entries) {
  return entries.map((entry, index) => ({
    ...entry,
    sort_order: index,
  }));
}

function moveEntry(entries, fromIndex, direction) {
  const toIndex = fromIndex + direction;
  if (fromIndex < 0 || toIndex < 0 || toIndex >= entries.length) return entries;
  const next = [...entries];
  [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
  return withSortOrder(next);
}

function visibleChannelsSummary(menu) {
  const visibility = normalizeMenuVisibility(menu?.scope, menu?.visibility);
  return SALES_MENU_CHANNEL_OPTIONS.filter((channel) => visibility[channel.value]).map((channel) => channel.label);
}

function inferPrintGroupsForContext({ menuName = '', groupName = '', articleName = '' }) {
  const haystack = [menuName, groupName, articleName]
    .filter(Boolean)
    .join(' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  if (BAR_KEYWORDS.some((keyword) => haystack.includes(keyword))) {
    return ['Bar'];
  }

  return ['Küche'];
}

function VisibilityToggle({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm transition-colors ${
        active
          ? 'border-[#132d39] bg-[#132d39] text-white'
          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
      }`}
    >
      <span className="font-medium">{label}</span>
      <span
        className={`ml-3 inline-flex h-5 w-5 items-center justify-center rounded-full ${
          active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-400'
        }`}
      >
        {active ? <Check className="h-3.5 w-3.5" /> : null}
      </span>
    </button>
  );
}

export default function AdminSalesCatalog({ embedded = false }) {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [products, setProducts] = useState([]);
  const [draftCatalog, setDraftCatalog] = useState(buildEmptySalesCatalog());
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [pickerGroupId, setPickerGroupId] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  const [newArticle, setNewArticle] = useState({ name: '', price: '0', vat_rate: '10' });
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingArticle, setIsCreatingArticle] = useState(false);

  const loadProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data || []);
    } catch (error) {
      setProducts([]);
      toast.error('Artikel konnten nicht geladen werden');
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    const nextCatalog = normalizeSalesCatalog(portalConfig?.salesCatalog || buildEmptySalesCatalog());
    setDraftCatalog(nextCatalog);
  }, [portalConfig]);

  useEffect(() => {
    if (!selectedMenuId || !draftCatalog.menus.some((menu) => menu.id === selectedMenuId)) {
      setSelectedMenuId(draftCatalog.menus[0]?.id || '');
    }
  }, [draftCatalog, selectedMenuId]);

  const selectedMenu = draftCatalog.menus.find((menu) => menu.id === selectedMenuId) || null;

  const saveCatalog = async () => {
    setIsSaving(true);
    try {
      const normalized = normalizeSalesCatalog(draftCatalog);
      await savePortalConfig(
        (previous) => ({
          ...previous,
          salesCatalog: normalized,
          menus: deriveLegacyMenusFromSalesCatalog(normalized),
        }),
        'Menüs und Kanalregeln gespeichert',
      );
      setDraftCatalog(normalized);
    } finally {
      setIsSaving(false);
    }
  };

  const updateMenu = (menuId, updater) => {
    setDraftCatalog((previous) => ({
      ...previous,
      menus: previous.menus.map((menu) => (menu.id === menuId ? updater(menu) : menu)),
    }));
  };

  const updateGroup = (menuId, groupId, updater) => {
    updateMenu(menuId, (menu) => ({
      ...menu,
      groups: menu.groups.map((group) => (group.id === groupId ? updater(group) : group)),
    }));
  };

  const addMenu = () => {
    const nextMenu = {
      id: createCatalogId('menu'),
      name: `Neues Menü ${draftCatalog.menus.length + 1}`,
      scope: 'restaurant',
      enabled: true,
      visibility: buildDefaultMenuVisibility('restaurant'),
      sort_order: draftCatalog.menus.length,
      groups: [],
    };
    setDraftCatalog((previous) => ({
      ...previous,
      menus: [...previous.menus, nextMenu],
    }));
    setSelectedMenuId(nextMenu.id);
  };

  const removeMenu = (menuId) => {
    if (!window.confirm('Dieses Menü wirklich löschen?')) return;
    const remaining = draftCatalog.menus.filter((menu) => menu.id !== menuId);
    setDraftCatalog((previous) => ({
      ...previous,
      menus: withSortOrder(previous.menus.filter((menu) => menu.id !== menuId)),
    }));
    if (selectedMenuId === menuId) {
      setSelectedMenuId(remaining[0]?.id || '');
    }
  };

  const moveMenu = (menuId, direction) => {
    const index = draftCatalog.menus.findIndex((menu) => menu.id === menuId);
    if (index < 0) return;
    setDraftCatalog((previous) => ({
      ...previous,
      menus: moveEntry(previous.menus, index, direction),
    }));
  };

  const toggleMenuVisibility = (menuId, channel) => {
    updateMenu(menuId, (menu) => {
      const nextVisibility = normalizeMenuVisibility(menu.scope, menu.visibility);
      nextVisibility[channel] = !nextVisibility[channel];
      return { ...menu, visibility: nextVisibility };
    });
  };

  const addGroup = () => {
    if (!selectedMenu) return;
    updateMenu(selectedMenu.id, (menu) => ({
      ...menu,
      groups: withSortOrder([
        ...menu.groups,
        {
          id: createCatalogId('group'),
          name: `Gruppe ${menu.groups.length + 1}`,
          enabled: true,
          sort_order: menu.groups.length,
          items: [],
        },
      ]),
    }));
  };

  const removeGroup = (groupId) => {
    if (!selectedMenu) return;
    if (!window.confirm('Diese Gruppe wirklich löschen?')) return;
    updateMenu(selectedMenu.id, (menu) => ({
      ...menu,
      groups: withSortOrder(menu.groups.filter((group) => group.id !== groupId)),
    }));
    if (pickerGroupId === groupId) {
      setPickerGroupId('');
      setPickerSearch('');
    }
  };

  const moveGroup = (groupId, direction) => {
    if (!selectedMenu) return;
    const index = selectedMenu.groups.findIndex((group) => group.id === groupId);
    if (index < 0) return;
    updateMenu(selectedMenu.id, (menu) => ({
      ...menu,
      groups: moveEntry(menu.groups, index, direction),
    }));
  };

  const addProductToGroup = (groupId, product) => {
    if (!selectedMenu || !product) return;
    updateGroup(selectedMenu.id, groupId, (group) => {
      if (group.items.some((item) => item.productId === product.id)) {
        return group;
      }
      return {
        ...group,
        items: withSortOrder([
          ...group.items,
          {
            id: createCatalogId('item'),
            productId: product.id,
            label: '',
            description: '',
            price: Number(product.price || 0),
            enabled: true,
            sort_order: group.items.length,
          },
        ]),
      };
    });
  };

  const moveItem = (groupId, itemId, direction) => {
    if (!selectedMenu) return;
    updateGroup(selectedMenu.id, groupId, (group) => {
      const index = group.items.findIndex((item) => item.id === itemId);
      if (index < 0) return group;
      return {
        ...group,
        items: moveEntry(group.items, index, direction),
      };
    });
  };

  const createArticleAndAdd = async (groupId) => {
    if (!newArticle.name.trim()) {
      toast.error('Bitte zuerst einen Artikelnamen eingeben');
      return;
    }

    const targetGroup = selectedMenu?.groups.find((group) => group.id === groupId);
    const inferredPrintGroups = inferPrintGroupsForContext({
      menuName: selectedMenu?.name || '',
      groupName: targetGroup?.name || '',
      articleName: newArticle.name.trim(),
    });

    setIsCreatingArticle(true);
    try {
      const created = await createProduct({
        name: newArticle.name.trim(),
        price: Number(newArticle.price || 0),
        category_id: null,
        description: '',
        vat_rate: Number(newArticle.vat_rate || 10),
        barcode: '',
        print_groups: inferredPrintGroups,
      });
      await loadProducts();
      addProductToGroup(groupId, created);
      setNewArticle({ name: '', price: '0', vat_rate: '10' });
      toast.success('Artikel erstellt und dem Menü hinzugefügt');
    } catch (error) {
      toast.error('Artikel konnte nicht erstellt werden');
    } finally {
      setIsCreatingArticle(false);
    }
  };

  const productMap = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'min-h-[220px]' : 'min-h-[60vh]'}`}>
        <p className="text-slate-500">Menüstruktur wird geladen…</p>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 p-6'}>
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400">Artikel Studio</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-[#132d39]">
            Menüs, Kanäle und Bonieren
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            Baue Menüs mit Gruppen und Artikeln auf, lege kanalbezogene Preise fest und steuere, wo ein Menü
            wirklich sichtbar ist: Kassa, Webshop, Bestellmonitor oder Partner.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={addMenu} className="rounded-2xl">
            <Plus className="mr-2 h-4 w-4" />
            Menü hinzufügen
          </Button>
          <Button onClick={saveCatalog} className="rounded-2xl bg-[#132d39] hover:bg-[#214250]" disabled={isSaving}>
            {isSaving ? 'Speichert…' : 'Menüs speichern'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[340px_1fr]">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="space-y-3 p-4">
            {draftCatalog.menus.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Noch kein Menü angelegt.
              </div>
            ) : null}

            {draftCatalog.menus.map((menu, index) => {
              const visibleChannels = visibleChannelsSummary(menu);
              const articleCount = menu.groups.reduce((sum, group) => sum + group.items.length, 0);

              return (
                <div
                  key={menu.id}
                  className={`rounded-3xl border p-4 transition-colors ${
                    selectedMenuId === menu.id
                      ? 'border-[#132d39] bg-[#132d39] text-white shadow-sm'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedMenuId(menu.id)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{menu.name}</p>
                          <p
                            className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                              selectedMenuId === menu.id
                                ? 'border-white/20 bg-white/10 text-white'
                                : scopeTone(menu.scope)
                            }`}
                          >
                            {SALES_MENU_SCOPE_LABELS[menu.scope]}
                          </p>
                        </div>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                            menu.enabled
                              ? selectedMenuId === menu.id
                                ? 'bg-emerald-400/20 text-white'
                                : 'bg-emerald-50 text-emerald-700'
                              : selectedMenuId === menu.id
                                ? 'bg-white/10 text-white/80'
                                : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {menu.enabled ? 'Aktiv' : 'Aus'}
                        </span>
                      </div>
                      <p className={`mt-3 text-xs ${selectedMenuId === menu.id ? 'text-white/70' : 'text-slate-500'}`}>
                        {menu.groups.length} Gruppen · {articleCount} Artikel
                      </p>
                      <p className={`mt-2 text-xs ${selectedMenuId === menu.id ? 'text-white/60' : 'text-slate-400'}`}>
                        {visibleChannels.length ? visibleChannels.join(' · ') : 'Kein Kanal aktiviert'}
                      </p>
                    </button>

                    <div className="flex shrink-0 flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveMenu(menu.id, -1)}
                        disabled={index === 0}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border disabled:cursor-not-allowed disabled:opacity-35 ${
                          selectedMenuId === menu.id
                            ? 'border-white/10 bg-white/5 text-current'
                            : 'border-slate-200 bg-white text-slate-600'
                        }`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMenu(menu.id, 1)}
                        disabled={index === draftCatalog.menus.length - 1}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border disabled:cursor-not-allowed disabled:opacity-35 ${
                          selectedMenuId === menu.id
                            ? 'border-white/10 bg-white/5 text-current'
                            : 'border-slate-200 bg-white text-slate-600'
                        }`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="space-y-6">
          {selectedMenu ? (
            <>
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="space-y-5 p-6">
                  <div className="grid gap-5 md:grid-cols-[1.1fr_0.9fr_auto]">
                    <div className="space-y-2">
                      <Label>Menüname</Label>
                      <Input
                        value={selectedMenu.name}
                        onChange={(event) =>
                          updateMenu(selectedMenu.id, (menu) => ({ ...menu, name: event.target.value }))
                        }
                        className="h-12 rounded-2xl border-slate-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Menütyp</Label>
                      <select
                        value={selectedMenu.scope}
                        onChange={(event) =>
                          updateMenu(selectedMenu.id, (menu) => ({
                            ...menu,
                            scope: event.target.value,
                            visibility: normalizeMenuVisibility(event.target.value, menu.visibility),
                          }))
                        }
                        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm"
                      >
                        {SALES_MENU_SCOPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end justify-between gap-3 md:justify-end">
                      <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600">
                        <input
                          type="checkbox"
                          checked={selectedMenu.enabled}
                          onChange={(event) =>
                            updateMenu(selectedMenu.id, (menu) => ({ ...menu, enabled: event.target.checked }))
                          }
                        />
                        Aktiv
                      </label>
                      <Button variant="outline" className="rounded-2xl text-red-600" onClick={() => removeMenu(selectedMenu.id)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Löschen
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-[#132d39]">Sichtbarkeit je Kanal</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Ein Menü kann vom Typ her zur Bestellung passen und trotzdem pro Kanal getrennt ein- oder ausgeblendet werden.
                      </p>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      {SALES_MENU_CHANNEL_OPTIONS.map((channel) => {
                        const visibility = normalizeMenuVisibility(selectedMenu.scope, selectedMenu.visibility);
                        return (
                          <VisibilityToggle
                            key={channel.value}
                            label={channel.label}
                            active={visibility[channel.value] !== false}
                            onClick={() => toggleMenuVisibility(selectedMenu.id, channel.value)}
                          />
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#132d39]">Gruppen und Artikel</h2>
                  <p className="text-sm text-slate-500">
                    Die Reihenfolge der Gruppen und Artikel steuert direkt den Haupt-Bestellschirm im POS und im Webshop.
                  </p>
                </div>
                <Button variant="outline" onClick={addGroup} className="rounded-2xl">
                  <Plus className="mr-2 h-4 w-4" />
                  Gruppe hinzufügen
                </Button>
              </div>

              {selectedMenu.groups.length === 0 ? (
                <Card className="border-dashed border-slate-300">
                  <CardContent className="p-8 text-center text-sm text-slate-500">
                    Dieses Menü hat noch keine Gruppen.
                  </CardContent>
                </Card>
              ) : null}

              {selectedMenu.groups.map((group, groupIndex) => {
                const groupProducts = group.items
                  .map((item) => ({ item, product: productMap.get(item.productId) }))
                  .filter((entry) => entry.product);

                const search = pickerSearch.trim().toLowerCase();
                const availableProducts = products.filter((product) => {
                  if (group.items.some((item) => item.productId === product.id)) return false;
                  if (!search) return true;
                  return `${product.name} ${product.description || ''}`.toLowerCase().includes(search);
                });

                return (
                  <Card key={group.id} className="border-slate-200 shadow-sm">
                    <CardContent className="space-y-5 p-6">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex flex-1 items-start gap-3">
                          <div className="flex flex-col gap-1 pt-7">
                            <button
                              type="button"
                              onClick={() => moveGroup(group.id, -1)}
                              disabled={groupIndex === 0}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveGroup(group.id, 1)}
                              disabled={groupIndex === selectedMenu.groups.length - 1}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="flex-1 space-y-2">
                            <Label>Gruppenname</Label>
                            <Input
                              value={group.name}
                              onChange={(event) =>
                                updateGroup(selectedMenu.id, group.id, (current) => ({
                                  ...current,
                                  name: event.target.value,
                                }))
                              }
                              className="h-11 rounded-2xl border-slate-200"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600">
                            <input
                              type="checkbox"
                              checked={group.enabled}
                              onChange={(event) =>
                                updateGroup(selectedMenu.id, group.id, (current) => ({
                                  ...current,
                                  enabled: event.target.checked,
                                }))
                              }
                            />
                            Aktiv
                          </label>
                          <Button variant="outline" className="rounded-2xl text-red-600" onClick={() => removeGroup(group.id)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Gruppe löschen
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {groupProducts.length === 0 ? (
                          <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">
                            Noch keine Artikel in dieser Gruppe.
                          </div>
                        ) : null}

                        {groupProducts.map(({ item, product }, itemIndex) => (
                          <div key={item.id} className="rounded-3xl border border-slate-200 bg-white p-4">
                            <div className="grid gap-4 xl:grid-cols-[auto_1fr_1fr_160px_auto]">
                              <div className="flex flex-col gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveItem(group.id, item.id, -1)}
                                  disabled={itemIndex === 0}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveItem(group.id, item.id, 1)}
                                  disabled={itemIndex === groupProducts.length - 1}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                              </div>
                              <div>
                                <p className="font-semibold text-[#132d39]">{product.name}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  Basispreis €{Number(product.price || 0).toFixed(2)} · MwSt {Number(product.vat_rate || 10).toFixed(0)}%
                                </p>
                              </div>
                              <div className="space-y-2">
                                <Label>Menüname am Artikel</Label>
                                <Input
                                  value={item.label}
                                  onChange={(event) =>
                                    updateGroup(selectedMenu.id, group.id, (current) => ({
                                      ...current,
                                      items: current.items.map((entry) =>
                                        entry.id === item.id ? { ...entry, label: event.target.value } : entry,
                                      ),
                                    }))
                                  }
                                  placeholder={product.name}
                                  className="h-10 rounded-2xl border-slate-200"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Kanalpreis</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={item.price}
                                  onChange={(event) =>
                                    updateGroup(selectedMenu.id, group.id, (current) => ({
                                      ...current,
                                      items: current.items.map((entry) =>
                                        entry.id === item.id ? { ...entry, price: Number(event.target.value || 0) } : entry,
                                      ),
                                    }))
                                  }
                                  className="h-10 rounded-2xl border-slate-200"
                                />
                              </div>
                              <div className="flex flex-col gap-2 xl:items-end">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateGroup(selectedMenu.id, group.id, (current) => ({
                                      ...current,
                                      items: current.items.map((entry) =>
                                        entry.id === item.id ? { ...entry, enabled: !entry.enabled } : entry,
                                      ),
                                    }))
                                  }
                                  className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700"
                                >
                                  {item.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                  {item.enabled ? 'Sichtbar' : 'Versteckt'}
                                </button>
                                <Button
                                  variant="outline"
                                  className="rounded-2xl text-red-600"
                                  onClick={() =>
                                    updateGroup(selectedMenu.id, group.id, (current) => ({
                                      ...current,
                                      items: withSortOrder(current.items.filter((entry) => entry.id !== item.id)),
                                    }))
                                  }
                                >
                                  Entfernen
                                </Button>
                              </div>
                            </div>

                            <div className="mt-4 space-y-2">
                              <Label>Beschreibung im Menü</Label>
                              <Textarea
                                value={item.description}
                                onChange={(event) =>
                                  updateGroup(selectedMenu.id, group.id, (current) => ({
                                    ...current,
                                    items: current.items.map((entry) =>
                                      entry.id === item.id ? { ...entry, description: event.target.value } : entry,
                                    ),
                                  }))
                                }
                                placeholder={product.description || 'Optionaler kanalbezogener Beschreibungstext'}
                                className="min-h-[90px] rounded-3xl border-slate-200"
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <p className="font-semibold text-[#132d39]">Artikel hinzufügen</p>
                            <p className="mt-1 text-sm text-slate-500">
                              Bestehenden Artikel wählen oder direkt hier einen neuen Artikel anlegen.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            className="rounded-2xl"
                            onClick={() => {
                              setPickerGroupId(pickerGroupId === group.id ? '' : group.id);
                              setPickerSearch('');
                            }}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            {pickerGroupId === group.id ? 'Ausblenden' : 'Artikel auswählen'}
                          </Button>
                        </div>

                        {pickerGroupId === group.id ? (
                          <div className="mt-5 grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
                            <div className="space-y-3">
                              <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <Input
                                  value={pickerSearch}
                                  onChange={(event) => setPickerSearch(event.target.value)}
                                  placeholder="Vorhandenen Artikel suchen"
                                  className="h-11 rounded-2xl border-slate-200 bg-white pl-10"
                                />
                              </div>
                              <div className="max-h-[320px] space-y-2 overflow-auto pr-1">
                                {availableProducts.map((product) => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onClick={() => addProductToGroup(group.id, product)}
                                    className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50"
                                  >
                                    <div>
                                      <p className="font-medium text-[#132d39]">{product.name}</p>
                                      <p className="mt-1 text-xs text-slate-500">
                                        Basispreis €{Number(product.price || 0).toFixed(2)}
                                      </p>
                                    </div>
                                    <span className="rounded-full bg-[#132d39] px-3 py-1 text-xs font-semibold text-white">
                                      Hinzufügen
                                    </span>
                                  </button>
                                ))}
                                {availableProducts.length === 0 ? (
                                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                                    Keine passenden Artikel gefunden.
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="rounded-3xl border border-slate-200 bg-white p-4">
                              <p className="font-semibold text-[#132d39]">Neuen Artikel direkt anlegen</p>
                              <div className="mt-4 space-y-3">
                                <div>
                                  <Label>Name</Label>
                                  <Input
                                    value={newArticle.name}
                                    onChange={(event) =>
                                      setNewArticle((previous) => ({ ...previous, name: event.target.value }))
                                    }
                                    className="mt-2 h-11 rounded-2xl border-slate-200"
                                  />
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div>
                                    <Label>Preis</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={newArticle.price}
                                      onChange={(event) =>
                                        setNewArticle((previous) => ({ ...previous, price: event.target.value }))
                                      }
                                      className="mt-2 h-11 rounded-2xl border-slate-200"
                                    />
                                  </div>
                                  <div>
                                    <Label>MwSt %</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={newArticle.vat_rate}
                                      onChange={(event) =>
                                        setNewArticle((previous) => ({ ...previous, vat_rate: event.target.value }))
                                      }
                                      className="mt-2 h-11 rounded-2xl border-slate-200"
                                    />
                                  </div>
                                </div>
                                <Button
                                  onClick={() => createArticleAndAdd(group.id)}
                                  className="w-full rounded-2xl bg-[#132d39] hover:bg-[#214250]"
                                  disabled={isCreatingArticle}
                                >
                                  {isCreatingArticle ? 'Erstellt…' : 'Artikel erstellen und hinzufügen'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          ) : (
            <Card className="border-dashed border-slate-300">
              <CardContent className="p-10 text-center text-slate-500">
                Wähle links ein Menü aus oder lege ein neues an.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
